import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@components/ui/button/Button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { TextArea } from '@shared/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { formatDate } from '@utils/FormatUtils';
import { createPopupSchema, type CreatePopupFormData, POPUP_VALIDATION } from '../../models/CreatePopupSchema';
import { getLastVisibleDate, getMinExpiresAtInputValue } from '../../utils/expiresAt';
import { isEnglishLocale } from '../../utils/popupLocale';

interface CreatePopupModalProps {
  onClose?: () => void;
  onSubmit: (data: CreatePopupFormData) => Promise<void> | void;
  isLoading: boolean;
}

export interface CreatePopupModalRef {
  open: () => void;
  close: () => void;
}

const DEFAULT_VALUES: CreatePopupFormData = {
  title: '',
  description: '',
  frequency: 'once',
  expires_at: '',
  title_en: '',
  description_en: '',
};

export const CreatePopupModal = forwardRef<CreatePopupModalRef, CreatePopupModalProps>(
  ({ onClose, onSubmit, isLoading }, ref) => {
    const { t, i18n } = useTranslation('popups');
    const modalRef = useRef<ModalCenterRef>(null);

    const {
      register,
      handleSubmit,
      control,
      formState: { errors },
      reset,
      watch,
    } = useForm<CreatePopupFormData>({
      resolver: zodResolver(createPopupSchema),
      defaultValues: DEFAULT_VALUES,
      mode: 'onBlur',
    });

    const titleLength = watch('title')?.length || 0;
    const descriptionLength = watch('description')?.length || 0;
    const titleEnLength = watch('title_en')?.length || 0;
    const descriptionEnLength = watch('description_en')?.length || 0;
    const expiresAtValue = watch('expires_at');
    const isEnglishUI = isEnglishLocale(i18n.resolvedLanguage ?? i18n.language);

    const expiresAtHint = (() => {
      if (!expiresAtValue) {
        return t('form.expiresAt.hint');
      }

      try {
        return t('form.expiresAt.hintWithDates', {
          lastVisibleDate: formatDate(getLastVisibleDate(expiresAtValue)),
          expiresDate: formatDate(expiresAtValue),
        });
      } catch {
        return t('form.expiresAt.hint');
      }
    })();

    useImperativeHandle(ref, () => ({
      open: () => {
        reset(DEFAULT_VALUES);
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
      },
    }));

    const handleInternalClose = () => {
      reset(DEFAULT_VALUES);
      onClose?.();
    };

    const onSubmitHandler = (data: CreatePopupFormData) => {
      Promise.resolve(onSubmit(data))
        .then(() => {
          reset(DEFAULT_VALUES);
          modalRef.current?.closeModal();
        })
        .catch(() => {
          // Errores gestionados en el hook useCreatePopup (toasts)
        });
    };

    const renderTitleField = (name: 'title' | 'title_en', label: string, placeholder: string, length: number) => (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={name}>
            {label} <span className="text-destructive">*</span>
          </Label>
          <span
            className={`text-xs ${length > POPUP_VALIDATION.TITLE_MAX_LENGTH ? 'text-destructive' : 'text-muted-foreground'}`}
          >
            {length}/{POPUP_VALIDATION.TITLE_MAX_LENGTH}
          </span>
        </div>
        <Input
          id={name}
          type="text"
          placeholder={placeholder}
          maxLength={POPUP_VALIDATION.TITLE_MAX_LENGTH}
          aria-invalid={!!errors[name]}
          {...register(name)}
        />
        {errors[name] && (
          <p className="text-sm text-destructive">
            {t(errors[name]?.message || '', {
              max: POPUP_VALIDATION.TITLE_MAX_LENGTH,
            })}
          </p>
        )}
      </div>
    );

    const renderDescriptionField = (
      name: 'description' | 'description_en',
      label: string,
      placeholder: string,
      length: number,
    ) => (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={name}>
            {label} <span className="text-destructive">*</span>
          </Label>
          <span
            className={`text-xs ${length > POPUP_VALIDATION.DESCRIPTION_MAX_LENGTH ? 'text-destructive' : 'text-muted-foreground'}`}
          >
            {length}/{POPUP_VALIDATION.DESCRIPTION_MAX_LENGTH}
          </span>
        </div>
        <TextArea
          id={name}
          placeholder={placeholder}
          rows={4}
          maxLength={POPUP_VALIDATION.DESCRIPTION_MAX_LENGTH}
          aria-invalid={!!errors[name]}
          {...register(name)}
        />
        {errors[name] && (
          <p className="text-sm text-destructive">
            {t(errors[name]?.message || '', {
              max: POPUP_VALIDATION.DESCRIPTION_MAX_LENGTH,
            })}
          </p>
        )}
      </div>
    );

    return (
      <ModalCenter
        ref={modalRef}
        title={t('modal.create.title')}
        type="default"
        width="xl"
        classNameBody="max-h-[calc(100vh-120px)] overflow-y-auto"
        onCloseModal={handleInternalClose}
        data-testid="create-popup-modal"
      >
        <form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {isEnglishUI ? (
              <>
                {renderTitleField('title_en', t('form.titleEn.label'), t('form.titleEn.placeholder'), titleEnLength)}
                {renderTitleField('title', t('form.titleEs.label'), t('form.title.placeholder'), titleLength)}
              </>
            ) : (
              <>
                {renderTitleField('title', t('form.titleEs.label'), t('form.title.placeholder'), titleLength)}
                {renderTitleField('title_en', t('form.titleEn.label'), t('form.titleEn.placeholder'), titleEnLength)}
              </>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="expires_at">
                {t('form.expiresAt.label')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="expires_at"
                type="date"
                min={getMinExpiresAtInputValue()}
                aria-describedby="expires-at-hint"
                aria-invalid={!!errors.expires_at}
                {...register('expires_at')}
              />
              <p id="expires-at-hint" className="text-xs leading-relaxed text-muted-foreground">
                {expiresAtHint}
              </p>
              {errors.expires_at && <p className="text-sm text-destructive">{t(errors.expires_at.message || '')}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="frequency">
                {t('form.frequency.label')} <span className="text-destructive">*</span>
              </Label>
              <Controller
                name="frequency"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="frequency" data-testid="popup-frequency-select">
                      <SelectValue placeholder={t('form.frequency.placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="once">{t('filters.frequency.once')}</SelectItem>
                      <SelectItem value="daily">{t('filters.frequency.daily')}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {isEnglishUI ? (
              <>
                {renderDescriptionField(
                  'description_en',
                  t('form.descriptionEn.label'),
                  t('form.descriptionEn.placeholder'),
                  descriptionEnLength,
                )}
                {renderDescriptionField(
                  'description',
                  t('form.descriptionEs.label'),
                  t('form.description.placeholder'),
                  descriptionLength,
                )}
              </>
            ) : (
              <>
                {renderDescriptionField(
                  'description',
                  t('form.descriptionEs.label'),
                  t('form.description.placeholder'),
                  descriptionLength,
                )}
                {renderDescriptionField(
                  'description_en',
                  t('form.descriptionEn.label'),
                  t('form.descriptionEn.placeholder'),
                  descriptionEnLength,
                )}
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => modalRef.current?.closeModal()}
              data-testid="create-popup-cancel-button"
            >
              {t('modal.create.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={isLoading}
              data-testid="create-popup-submit-button"
            >
              {isLoading ? t('modal.create.submitting') : t('modal.create.submit')}
            </Button>
          </div>
        </form>
      </ModalCenter>
    );
  },
);

CreatePopupModal.displayName = 'CreatePopupModal';
